/**
 * ToolSelectionTree — hierarchical tool selection for the process editor.
 * Groups discovered tools by mcp_id. Step mode persists include/exclude lists;
 * response mode persists excluded regular tools.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ToolSelection } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

interface ToolEntry {
  name: string;
  description: string;
  mcp_id: string;
}

interface ToolGroup {
  id: string;
  label: string;
  tools: ToolEntry[];
}

export interface ToolSelectionTreeProps {
  value: ToolSelection | undefined;
  onChange: (next: ToolSelection | undefined) => void;
  disabled?: boolean;
  mode?: 'step' | 'response';
  excludedTools?: string[];
  onExcludedToolsChange?: (tools: string[]) => void;
  testIdPrefix?: string;
  catalogTools?: ToolEntry[];
}

type SelectionMode = 'all' | 'include' | 'exclude';

function computeSelectionMode(value: ToolSelection | undefined): SelectionMode {
  if (!value) return 'all';
  if (value.include && value.include.length > 0) return 'include';
  if (value.exclude && value.exclude.length > 0) return 'exclude';
  return 'all';
}

export function ToolSelectionTree({
  value,
  onChange,
  disabled = false,
  mode = 'step',
  excludedTools = [],
  onExcludedToolsChange,
  testIdPrefix = 'tool-tree',
  catalogTools,
}: ToolSelectionTreeProps) {
  const [tools, setTools] = useState<ToolEntry[]>(catalogTools ?? []);
  const [loading, setLoading] = useState(catalogTools === undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const selectionMode = computeSelectionMode(value);

  useEffect(() => {
    if (catalogTools !== undefined) {
      setTools(catalogTools);
      setLoading(false);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    try {
      getSDK()
        .tools.listAvailableTools()
        .then((res) => {
          if (!cancelled) setTools(res.tools);
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : String(err));
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } catch (err: unknown) {
      if (!cancelled) {
        setLoadError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [catalogTools]);

  const groups = useMemo((): ToolGroup[] => {
    const mcpGroups = new Map<string, ToolEntry[]>();
    for (const tool of tools) {
      const existing = mcpGroups.get(tool.mcp_id) ?? [];
      existing.push(tool);
      mcpGroups.set(tool.mcp_id, existing);
    }

    const result: ToolGroup[] = [];
    for (const [mcpId, mcpTools] of mcpGroups) {
      result.push({
        id: mcpId,
        label: mcpId,
        tools: mcpTools.sort((a, b) => a.name.localeCompare(b.name)),
      });
    }
    return result;
  }, [tools]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const isToolSelected = (toolName: string): boolean => {
    if (mode === 'response') {
      return !excludedTools.includes(toolName);
    }

    if (selectionMode === 'all') return true;
    if (selectionMode === 'include') {
      return value?.include?.includes(toolName) ?? false;
    }
    return !value?.exclude?.includes(toolName);
  };

  const handleToolToggle = (toolName: string, checked: boolean) => {
    if (mode === 'response') {
      if (checked) {
        onExcludedToolsChange?.(excludedTools.filter((t) => t !== toolName));
      } else {
        onExcludedToolsChange?.([...excludedTools, toolName]);
      }
      return;
    }

    if (selectionMode === 'all') {
      if (!checked) {
        onChange({ exclude: [toolName] });
      }
    } else if (selectionMode === 'include') {
      const currentInclude = value?.include ?? [];
      if (checked) {
        onChange({ include: [...currentInclude, toolName] });
      } else {
        const next = currentInclude.filter((t) => t !== toolName);
        onChange(next.length > 0 ? { include: next } : undefined);
      }
    } else {
      const currentExclude = value?.exclude ?? [];
      if (checked) {
        const next = currentExclude.filter((t) => t !== toolName);
        onChange(next.length > 0 ? { exclude: next } : undefined);
      } else {
        onChange({ exclude: [...currentExclude, toolName] });
      }
    }
  };

  const getGroupCheckState = (group: ToolGroup): 'checked' | 'unchecked' | 'indeterminate' => {
    const selectedCount = group.tools.filter((t) => isToolSelected(t.name)).length;
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === group.tools.length) return 'checked';
    return 'indeterminate';
  };

  const handleGroupToggle = (group: ToolGroup) => {
    const state = getGroupCheckState(group);
    const shouldSelect = state !== 'checked';
    const toolNames = group.tools.map((t) => t.name);

    if (mode === 'response') {
      if (shouldSelect) {
        onExcludedToolsChange?.(excludedTools.filter((t) => !toolNames.includes(t)));
      } else {
        onExcludedToolsChange?.([...new Set([...excludedTools, ...toolNames])]);
      }
      return;
    }

    if (selectionMode === 'all') {
      if (!shouldSelect) {
        onChange({ exclude: toolNames });
      }
    } else if (selectionMode === 'include') {
      const currentInclude = value?.include ?? [];
      if (shouldSelect) {
        onChange({ include: [...new Set([...currentInclude, ...toolNames])] });
      } else {
        const next = currentInclude.filter((t) => !toolNames.includes(t));
        onChange(next.length > 0 ? { include: next } : undefined);
      }
    } else {
      const currentExclude = value?.exclude ?? [];
      if (shouldSelect) {
        const next = currentExclude.filter((t) => !toolNames.includes(t));
        onChange(next.length > 0 ? { exclude: next } : undefined);
      } else {
        onChange({ exclude: [...new Set([...currentExclude, ...toolNames])] });
      }
    }
  };

  if (loading) {
    return (
      <LoadingState variant="inline" label="Loading tools..." className="rounded-md bg-muted p-3" />
    );
  }

  return (
    <div
      data-testid={testIdPrefix}
      className="rounded-md bg-muted text-xs"
    >
      {loadError && (
        <div
          data-testid={`${testIdPrefix}-error`}
          className="rounded-t-md bg-destructive/10 p-2 text-destructive"
        >
          Failed to load MCP tools: {loadError}
        </div>
      )}
      <div className="max-h-64 overflow-y-auto p-2 space-y-1">
        {groups.map((group) => {
          const expanded = expandedGroups.has(group.id);
          const checkState = getGroupCheckState(group);

          return (
            <div key={group.id} data-testid={`${testIdPrefix}-group-${group.id}`}>
              <div className="flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-background/60">
                <GroupCheckbox
                  state={checkState}
                  onChange={() => handleGroupToggle(group)}
                  disabled={disabled}
                  testId={`${testIdPrefix}-group-checkbox-${group.id}`}
                />
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="flex flex-1 items-center gap-1 text-left font-medium"
                  disabled={disabled}
                >
                  <span className="text-muted-foreground">{expanded ? '▼' : '▶'}</span>
                  {group.label}
                  <span className="ml-1 text-muted-foreground">({group.tools.length})</span>
                </button>
              </div>
              {expanded && (
                <div className="ml-5 space-y-0.5 border-l pl-2">
                  {group.tools.map((tool) => (
                    <label
                      key={tool.name}
                      className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 hover:bg-background/60"
                    >
                      <input
                        type="checkbox"
                        data-testid={`${testIdPrefix}-tool-${tool.name}`}
                        checked={isToolSelected(tool.name)}
                        onChange={(e) => handleToolToggle(tool.name, e.target.checked)}
                        disabled={disabled}
                        className="h-3 w-3 rounded border-gray-300"
                      />
                      <span className="truncate" title={tool.description}>
                        {tool.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface GroupCheckboxProps {
  state: 'checked' | 'unchecked' | 'indeterminate';
  onChange: () => void;
  disabled?: boolean;
  testId?: string;
}

function GroupCheckbox({ state, onChange, disabled, testId }: GroupCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === 'indeterminate';
    }
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      data-testid={testId}
      checked={state === 'checked'}
      onChange={onChange}
      disabled={disabled}
      className="h-3 w-3 rounded border-gray-300"
    />
  );
}
